import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useActions, useCreateAction, useTransitionAction, type Action } from "../api/hooks";
import { Button, Chip, Empty, ErrorBox, Field, Loading, Page, Table, statusTone } from "../components/ui";
import { fmtNumber, relative, title } from "../lib/format";
import { audienceLabel, usePersonNames } from "../lib/names";

export const Route = createFileRoute("/actions")({ component: Actions });

const next: Record<string, { label: string; state: string }[]> = {
  recommended: [{ label: "Assign", state: "assigned" }, { label: "Dismiss", state: "dismissed" }],
  awaiting_approval: [{ label: "Approve", state: "assigned" }, { label: "Dismiss", state: "dismissed" }],
  assigned: [{ label: "Accept", state: "accepted" }, { label: "Dismiss", state: "dismissed" }],
  accepted: [{ label: "Start", state: "in_progress" }],
  in_progress: [{ label: "Complete", state: "completed" }, { label: "Dismiss", state: "dismissed" }],
};

function Actions() {
  const [filter, setFilter] = useState<"open" | "done">("open");
  const nameOf = usePersonNames();
  const actions = useActions(filter === "open" ? { state: "recommended,awaiting_approval,assigned,accepted,in_progress" } : { state: "completed,dismissed" });
  const transition = useTransitionAction();
  const [error, setError] = useState<unknown>(null);
  const [creating, setCreating] = useState(false);
  return (
    <Page
      title="Actions"
      sub="One engine for Workforce Intelligence and Management: recommended → assigned → accepted → in progress → completed."
      actions={
        <>
          <Button variant={filter === "open" ? "dark" : "light"} onClick={() => setFilter("open")}>Open</Button>
          <Button variant={filter === "done" ? "dark" : "light"} onClick={() => setFilter("done")}>Closed</Button>
          <Button variant="light" onClick={() => setCreating((v) => !v)}>{creating ? "Cancel" : "+ New action"}</Button>
        </>
      }
    >
      {error != null && <div className="mb-3"><ErrorBox error={error} /></div>}
      {creating && <NewAction onDone={() => setCreating(false)} onError={setError} />}
      {actions.isPending ? (
        <Loading />
      ) : actions.isError ? (
        <ErrorBox error={actions.error} />
      ) : actions.data.length === 0 ? (
        <Empty title={filter === "open" ? "No open actions" : "Nothing closed yet"} />
      ) : (
        <Table
          rows={actions.data}
          keyOf={(a) => a.id}
          cols={[
            { h: "Action", cell: (a: Action) => <><span className="font-semibold">{a.title}</span>{a.rationale && <span className="sub">{a.rationale}</span>}</> },
            { h: "Audience", cell: (a) => <span className="text-muted">{title(a.audience_type)} · {audienceLabel(a.audience_type, a.audience_key, nameOf)}</span>, w: "180px" },
            { h: "Owner", cell: (a) => <span className="text-muted">{nameOf(a.owner_person_key)}</span>, w: "140px" },
            { h: "SKU", cell: (a) => <span className="text-muted uppercase text-[11px] font-bold">{a.sku}</span>, w: "70px" },
            { h: "State", cell: (a) => <Chip tone={statusTone(a.state)}>{title(a.state)}</Chip>, w: "150px" },
            ...(filter === "done" ? [{ h: "Outcome", w: "260px", cell: (a: Action) => <Outcome action={a} /> }] : []),
            { h: "Updated", cell: (a) => <span className="text-muted">{relative(a.updated_at)}</span>, w: "100px" },
            {
              h: "",
              w: "200px",
              cell: (a) => (
                <div className="flex gap-1.5 justify-end">
                  {(next[a.state] ?? []).map((n) => (
                    <Button key={n.state} variant="light" className="!py-1 !px-2.5 !text-[12px]" disabled={transition.isPending} onClick={() => transition.mutate({ id: a.id, state: n.state }, { onError: setError })}>{n.label}</Button>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}
    </Page>
  );
}

function NewAction({ onDone, onError }: { onDone: () => void; onError: (e: unknown) => void }) {
  const create = useCreateAction();
  const [f, setF] = useState({ title: "", rationale: "", audience_type: "team", audience_key: "", owner_person_key: "", sku: "wi" });
  return (
    <form
      className="card grid grid-cols-3 gap-3 mb-4"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate({ ...f, rationale: f.rationale || null, owner_person_key: f.owner_person_key || null }, { onSuccess: onDone, onError });
      }}
    >
      <Field label="Title"><input className="input" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
      <Field label="Rationale"><input className="input" value={f.rationale} onChange={(e) => setF({ ...f, rationale: e.target.value })} /></Field>
      <Field label="SKU">
        <select className="input" value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })}><option value="wi">Workforce Intelligence</option><option value="management">Management</option></select>
      </Field>
      <Field label="Audience type">
        <select className="input" value={f.audience_type} onChange={(e) => setF({ ...f, audience_type: e.target.value })}>{["company", "department", "team", "person"].map((t) => <option key={t} value={t}>{title(t)}</option>)}</select>
      </Field>
      <Field label="Audience key"><input className="input" required placeholder="team key, department name, person key or 'all'" value={f.audience_key} onChange={(e) => setF({ ...f, audience_key: e.target.value })} /></Field>
      <Field label="Owner (person key)"><input className="input" value={f.owner_person_key} onChange={(e) => setF({ ...f, owner_person_key: e.target.value })} /></Field>
      <div className="col-span-3 flex gap-2"><Button type="submit" disabled={create.isPending}>Create</Button><Button type="button" variant="light" onClick={onDone}>Cancel</Button></div>
    </form>
  );
}

type Measured = { signal_key?: string; before?: number; after?: number; delta?: number; improved?: boolean; measured_period_start?: string };

/** Before → after on the action's own signal, 14 days after completion. */
function Outcome({ action }: { action: Action }) {
  const m = (action.measured_outcome ?? null) as Measured | null;
  if (action.state !== "completed") return <span className="text-muted">—</span>;
  if (!m || m.before == null || m.after == null) return <span className="text-muted">{action.baseline ? "Measured 14 days after completion" : "No baseline"}</span>;
  const unit = m.signal_key?.endsWith("_pct") ? "%" : "";
  return (
    <div>
      <div className="flex items-center gap-2"><span className="font-semibold">{fmtNumber(m.before)}{unit} → {fmtNumber(m.after)}{unit}</span><Chip tone={m.improved ? "good" : "high"}>{m.improved ? "Improved" : "No improvement"}</Chip></div>
      <span className="sub">{title(m.signal_key ?? "").replace(" Pct", " %")}{action.expected_outcome ? ` · aim: ${action.expected_outcome}` : ""}</span>
    </div>
  );
}
